import _ from 'lodash';
import { useLog } from '@tradingblock/components';
import { TradingEnvironments, TradingEnvironment } from './constants';

const isTruthyValue = (value: string | undefined) =>
  value && ['1', 'true', 'True', 'TRUE', 'yes'].includes(value) ? true : false;

function valueOrDefault<T = string>(key: string, getValue?: (val: string | undefined) => T | undefined) {
  return (defaultValue: T): T => {
    const keyVal = isTruthyValue(process.env.REACT_APP_IS_BETA) ? `REACT_APP_BETA_${key}` : `REACT_APP_${key}`;
    const strVal = process.env[keyVal];
    const envVal: T | undefined = getValue ? getValue(strVal) : (strVal as any);
    return _.isNil(envVal) ? defaultValue : envVal;
  };
}
const envConfig = {
  schemaVersion: process.env.REACT_APP_SCHEMA_VERSION as string,
  uiApi: `${process.env.REACT_APP_UI_API_URL as string}${
    (isTruthyValue(process.env.REACT_APP_IS_VIRTUAL) as boolean) ? '/virtual' : ''
  }`,
  tradingApi: process.env.REACT_APP_TRADING_API_URL as string,
  tradingApiWebsockets: process.env.REACT_APP_TRADING_API_WS_URL as string,
  tradingApiWebsocketsNew: process.env.REACT_APP_TRADING_API_WS_URL_NEW as string,
  marketNewsWebsockets: process.env.REACT_APP_MARKET_NEWS_WS_URL as string,
  orderStatusWebsockets: process.env.REACT_APP_ORDER_STATUS_WS_URL as string,
  expiringTokensWebsockets: process.env.REACT_APP_EXPIRING_TOKENS_WS_URL as string,
  cashieringWebsockets: process.env.REACT_APP_CASHIERING_WS_URL as string,
  loginCountWebsockets: process.env.REACT_APP_LOGIN_COUNT_WS_URL as string,
  authRedirect: process.env.REACT_APP_AUTH_REDIRECT as string,
  loginUrl: process.env.REACT_APP_LOGIN_URL as string,
  applicationUrl: process.env.REACT_APP_APPLICATION_URL as string,
  liveTradingUrl: process.env.REACT_APP_LIVE_URL as string,
  virtualTradingUrl: process.env.REACT_APP_VIRTUAL_URL as string,
  isProduction: process.env.REACT_APP_ENVIRONMENT === 'production',
  gaUaTrackingCode: process.env.REACT_APP_GA_UA_TRACKING_CODE as string,
  ga4TrackingCode: (process.env.REACT_APP_GA_4_TRACKING_CODE as string) || '',
  ga4VTTrackingCode: (process.env.REACT_APP_VT_GA_4_TRACKING_CODE as string) || '',
  eventLogWhitelist: ['@order/created'],
  isVirtual: isTruthyValue(process.env.REACT_APP_IS_VIRTUAL as string),
  subdirectory: (process.env.REACT_APP_TB_SUBDIRECTORY as string)
    ? (process.env.REACT_APP_TB_SUBDIRECTORY as string)
    : '',
  siteGroup: process.env.REACT_APP_TB_SITE_GROUP as string,
  perf: {
    batchedQuotes:
      process.env.REACT_APP_PERF_BATCHED_QUOTES === undefined
        ? true
        : isTruthyValue(process.env.REACT_APP_PERF_BATCHED_QUOTES as string),
    quotesBatchIntervalMs: (() => {
      const v = Number(process.env.REACT_APP_QUOTES_BATCH_INTERVAL_MS || '');
      return Number.isFinite(v) && v > 0 ? v : 400;
    })(),
  },
};

const notOverridableConfig = {
  TradingEnvironment: valueOrDefault<TradingEnvironments>(
    'TRADING_ENVIRONMENT',
    (val: string | undefined): TradingEnvironments => {
      const tEnv = _.toLower(val || '');
      switch (tEnv) {
        case 'local':
          return TradingEnvironment.local;
        case 'dev':
        case 'development':
        case 'staging':
          return TradingEnvironment.dev;
        case 'beta':
          return TradingEnvironment.beta;
        default:
          return TradingEnvironment.production;
      }
    }
  )(TradingEnvironment.dev),
};

const combinedConfig = {
  ...envConfig,
  ...notOverridableConfig,
};
const log = useLog('config');
log('using loaded config %o', combinedConfig);
type EnvConfigType = typeof envConfig;
type NotOverridableConfig = typeof notOverridableConfig;
export const Config: EnvConfigType & NotOverridableConfig = combinedConfig;
export default Config;
